import moment from "moment";
import type { StoreReconciliationAuditShiftModel } from "models";

const getDateFromYmd = (value: unknown) => {
  if (!value) {
    return null;
  }

  const parsed = moment(String(value), "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed : null;
};

const getTimeFromClock = (value: unknown) => {
  if (!value) {
    return null;
  }

  const parsed = moment(String(value), ["HH:mm:ss", "HH:mm"], true);
  return parsed.isValid() ? parsed : null;
};

const combineDateAndTime = (dateValue: unknown, timeValue: unknown) => {
  const parsedDate = getDateFromYmd(dateValue);
  const parsedTime = getTimeFromClock(timeValue);

  if (!parsedDate || !parsedTime) {
    return null;
  }

  return parsedDate
    .clone()
    .hour(parsedTime.hour())
    .minute(parsedTime.minute())
    .second(parsedTime.second());
};

const formatAuditTime = (shift: StoreReconciliationAuditShiftModel) => {
  const parsed = shift.reportDatetime ? moment.parseZone(shift.reportDatetime) : null;
  return parsed?.isValid() ? parsed.format("M/D h:mm A") : "Time unavailable";
};

const getShiftStartMoment = (shift: StoreReconciliationAuditShiftModel, reportDate?: string | null) => {
  return (
    combineDateAndTime(shift.shiftStartDate || reportDate, shift.shiftStartTime) ||
    (shift.reportDatetime ? moment.parseZone(shift.reportDatetime) : null)
  );
};

const getShiftEndMoment = (shift: StoreReconciliationAuditShiftModel, reportDate?: string | null) => {
  return (
    combineDateAndTime(shift.shiftEndDate || reportDate, shift.shiftEndTime) ||
    (shift.reportDatetime ? moment.parseZone(shift.reportDatetime) : null)
  );
};

export const formatShiftRangeLabel = (shift: StoreReconciliationAuditShiftModel, reportDate?: string | null) => {
  const shiftStart = getShiftStartMoment(shift, reportDate);
  const shiftEnd = getShiftEndMoment(shift, reportDate);

  if (!shiftStart?.isValid()) {
    return formatAuditTime(shift);
  }

  const startLabel = shiftStart.format("M/D h:mm A");

  if (!shiftEnd?.isValid()) {
    return `${startLabel} -`;
  }

  return `${startLabel} - ${shiftEnd.format("ddd, M/D h:mm A")}`;
};

export const formatShiftBreakdownWindow = (
  shifts: StoreReconciliationAuditShiftModel[],
  reportDate?: string | null
) => {
  if (!shifts.length) {
    return "";
  }

  const firstShift = shifts[0];
  const lastShift = shifts[shifts.length - 1];
  const start = getShiftStartMoment(firstShift, reportDate);
  const end = getShiftEndMoment(lastShift, reportDate);

  if (start?.isValid() && end?.isValid()) {
    return `${start.format("M/D h:mm A")} - ${end.format("M/D h:mm A")}`;
  }

  if (start?.isValid()) {
    return start.format("M/D h:mm A");
  }

  if (end?.isValid()) {
    return end.format("M/D h:mm A");
  }

  return "";
};

export const getShiftOperatorLabel = (shift: StoreReconciliationAuditShiftModel) =>
  shift.userName || shift.operatorName || "-";
